import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@structural-climbing/ui'
import type { GymBranding } from './useGymBranding'
import { isGymOpen } from './gymHours'
import { WorkingHoursModal } from './WorkingHoursModal'
import { GymMapModal } from './GymMapModal'
import { GymsSheet } from './GymsSheet'
import { IdeasTrophy } from '../ideas/IdeasTrophy'

interface Props {
  branding: GymBranding | null
  occupancy?: number
  /** The gym's slug: with it, tapping the gym opens "Sălile tale". */
  gymSlug?: string | null
  className?: string
}

/**
 * The gym, in one line, at the top of Home.
 *
 * This replaced a 144px cover photo with the logo overlapping it. That photo,
 * the greeting and the install banner filled the whole first screen, and the
 * check-in code started below the fold: the one thing people open Home for.
 *
 * Tapping the logo or the name opens "Sălile tale": this gym's cover, the way
 * to its own page, and your other gyms. The cover photo lives there, so it
 * never pushes the code down. The map is the icon on the right.
 */
export function GymBar({
  branding,
  occupancy,
  gymSlug,
  className = 'mx-auto max-w-xl px-4 pt-4',
}: Props) {
  const { t } = useTranslation()
  const [showHours, setShowHours] = useState(false)
  const [showGyms, setShowGyms] = useState(false)
  const [showMap, setShowMap] = useState(false)

  // Nothing to show until the gym's name has arrived.
  if (!branding?.name) return null

  const initials = branding.name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase()

  const hasHours = Boolean(branding.working_hours && branding.timezone)
  const gymOpen = hasHours && isGymOpen(branding.working_hours, branding.timezone)
  const hasMap = Boolean(branding.map_url && branding.map_file_type)

  const identity = (
    <>
      <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-[var(--gym-primary)] bg-content2 text-sm font-bold">
        {branding.image_url ? (
          <img
            src={branding.image_url}
            alt={`${branding.name} logo`}
            className="h-full w-full object-cover"
          />
        ) : (
          <span className="text-foreground/60">{initials}</span>
        )}
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-1 text-[15px] font-bold leading-tight">
          <span className="truncate">{branding.name}</span>
          {gymSlug && (
            <span aria-hidden className="shrink-0 text-xs text-foreground/40">
              ▾
            </span>
          )}
        </span>
        {/* Two pills that never break inside: open or closed, then how full
            it is. The old single line wrapped "Deschis acum" onto two lines
            on a small phone and cut the count off. */}
        {hasHours && (
          <span className="mt-1 flex min-w-0 items-center gap-1.5 overflow-hidden">
            <span
              data-testid="gym-status"
              className={`flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold ${
                gymOpen ? 'bg-success/15 text-success-700' : 'bg-danger/10 text-danger'
              }`}
            >
              <span
                aria-hidden
                className={`h-1.5 w-1.5 rounded-full ${gymOpen ? 'bg-success' : 'bg-danger'}`}
              />
              {gymOpen ? t('gymHours.openShort') : t('gymHours.closed')}
            </span>
            {gymOpen && (occupancy ?? 0) > 3 && (
              <span className="flex min-w-0 items-center gap-1 truncate rounded-full bg-foreground/[0.06] px-2 py-0.5 text-xs font-bold text-foreground/75">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.2}
                  strokeLinecap="round"
                  className="h-3.5 w-3.5 shrink-0"
                  aria-hidden="true"
                >
                  <circle cx="9" cy="8" r="3.2" />
                  <path d="M3.5 19c.6-3.2 2.8-5 5.5-5s4.9 1.8 5.5 5" />
                  <circle cx="17" cy="9" r="2.4" />
                  <path d="M16 14.2c2.4.1 4 1.7 4.5 4.8" />
                </svg>
                <span className="truncate">{t('gymSpace.peopleIn', { count: occupancy })}</span>
              </span>
            )}
          </span>
        )}
      </span>
    </>
  )

  return (
    <div className={`flex w-full items-center gap-2 ${className}`}>
      {gymSlug ? (
        <button
          type="button"
          onClick={() => setShowGyms(true)}
          aria-label={`${branding.name}. ${t('gymSpace.openGyms')}`}
          className="flex min-w-0 flex-1 items-center gap-3 rounded-lg text-left"
        >
          {identity}
        </button>
      ) : hasHours ? (
        <button
          type="button"
          onClick={() => setShowHours(true)}
          aria-label={`${branding.name}. ${t('gymHours.tapToSeeHours')}`}
          className="flex min-w-0 flex-1 items-center gap-3 rounded-lg text-left"
        >
          {identity}
        </button>
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-3">{identity}</div>
      )}

      {/* The bar's icons sit together, with no gap beyond each button's own
          tap area: the bar's gap between them read as two separate groups. */}
      <div className="-mr-1 flex shrink-0 items-center -space-x-1">
        {gymSlug && <IdeasTrophy gymSlug={gymSlug} />}

        {hasMap && (
          <Button
            isIconOnly
            variant="light"
            radius="full"
            aria-label={t('dashboard.seeGymMap')}
            onPress={() => setShowMap(true)}
            className="shrink-0 text-foreground/55"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-5 w-5"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M8.157 2.176a1.5 1.5 0 0 0-1.147 0l-4.084 1.69A1.5 1.5 0 0 0 2 5.25v10.877a1.5 1.5 0 0 0 2.074 1.386l3.51-1.452 4.26 1.762a1.5 1.5 0 0 0 1.146 0l4.083-1.69A1.5 1.5 0 0 0 18 14.75V3.872a1.5 1.5 0 0 0-2.073-1.386l-3.51 1.452-4.26-1.762ZM7.58 5a.75.75 0 0 1 .75.75v6.5a.75.75 0 0 1-1.5 0v-6.5A.75.75 0 0 1 7.58 5Zm5.59 2.75a.75.75 0 0 0-1.5 0v6.5a.75.75 0 0 0 1.5 0v-6.5Z"
                clipRule="evenodd"
              />
            </svg>
          </Button>
        )}
      </div>

      {/* Portalled to the body: on a phone this bar lives in the sticky header,
          whose blur makes it the frame for anything fixed inside it. The hours
          card then opened pinned to the header, behind no backdrop you could
          tap, and could not be closed. */}
      {hasHours &&
        createPortal(
          <WorkingHoursModal
            workingHours={branding.working_hours!}
            timezone={branding.timezone!}
            isOpen={showHours}
            onClose={() => setShowHours(false)}
          />,
          document.body,
        )}
      {gymSlug &&
        createPortal(
          <GymsSheet
            isOpen={showGyms}
            onClose={() => setShowGyms(false)}
            branding={branding}
            gymSlug={gymSlug}
            occupancy={occupancy}
          />,
          document.body,
        )}
      {hasMap && (
        <GymMapModal
          mapUrl={branding.map_url!}
          mapFileType={branding.map_file_type!}
          isOpen={showMap}
          onClose={() => setShowMap(false)}
        />
      )}
    </div>
  )
}
